import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { openPdf } from '../utils/pdf';
import { useToast } from '../components/Toast';
import { BeoordelingModal, type BeoordeelActie } from '../components/BeoordelingModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString('nl-NL') : '—');

const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept', issued: 'Verzonden', pending_check: 'Toetsing', insured: 'Verzekerd',
  paid: 'Betaald', claimed: 'Geclaimd', overdue: 'Vervallen', disputed: 'Betwist',
  submitted: 'Ingediend', under_review: 'In behandeling', approved: 'Goedgekeurd', denied: 'Afgewezen',
};

export const AdminMemberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/admin/members/${id}`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : null)).then(setD).finally(() => setLoading(false));
  }, [isAuthenticated, accessToken, navigate, id]);

  const refresh = () => fetch(`${API_BASE_URL}/api/admin/members/${id}`, { headers: { Authorization: `Bearer ${accessToken}` } })
    .then((r) => (r.ok ? r.json() : null)).then(setD);

  if (loading) return <Layout><div className="text-center py-12 text-slate-500">Laden...</div></Layout>;
  if (!d || !d.member) return <Layout><div className="text-center py-12 text-slate-500">Lid niet gevonden.</div></Layout>;

  const f = d.financials;
  const usageBar = (used: number, max: number, label: string) => {
    const over = max > 0 && used > max;
    const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          {over && <span className="text-xs font-semibold text-mfb-orange">bijbetaling</span>}
        </div>
        <p className="text-2xl font-bold mt-1 text-mfb-blue">{used}<span className="text-base font-medium text-slate-400"> / {max}</span></p>
        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${over ? 'bg-mfb-orange' : pct >= 80 ? 'bg-amber-500' : 'bg-mfb-blue'}`} style={{ width: `${over ? 100 : pct}%` }} /></div>
      </div>
    );
  };

  const Section: React.FC<{ title: string; cols: string[]; children: React.ReactNode }> = ({ title, cols, children }) => (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-slate-200 font-display text-lg font-semibold text-mfb-blue">{title}</div>
      <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50"><tr>{cols.map((c) => <th key={c} className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase">{c}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table></div>
    </div>
  );

  return (
    <Layout>
      <button onClick={() => navigate(`/backoffice/collectivities/${d.member.collectivity.id}`)} className="text-sm text-mfb-blue hover:underline mb-4">← Terug naar leden</button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-semibold text-mfb-blue">{d.member.company_name}</h1>
            <p className="text-sm text-slate-500">{d.member.full_name} · {d.member.email} · KvK {d.member.kvk || '—'} · Collectiviteit {d.member.collectivity.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => openPdf(`/api/admin/members/${id}/afrekening`, accessToken!)}
              className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg text-sm font-semibold transition shadow-sm whitespace-nowrap">
              📄 Afrekening (PDF)
            </button>
            <MemberStatusBadge status={d.contract.status} />
          </div>
        </div>
        <MemberStatusActions
          memberId={id!} token={accessToken!} status={d.contract.status}
          rol={d.contract.rol} onChanged={refresh} />
      </div>

      <AfrekeningenSection memberId={id!} token={accessToken!} />

      {/* Contract + Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Contract</h2>
          <dl className="space-y-3 text-sm">
            <Row label="Pakket" value={d.contract.pakket || '—'} />
            <Row label="Type" value={d.contract.verzekeren ? 'Volledig (verzekeren)' : 'Ratings-only'} />
            <Row label="Looptijd" value={`${fmtDate(d.contract.start_date)} t/m ${fmtDate(d.contract.end_date)}`} />
            <Row label="Telefoon" value={d.contract.telefoon || '—'} />
            <Row label="Rol" value={d.contract.rol} />
            <Row label="Laatste login" value={d.contract.last_login ? d.contract.last_login.replace('T', ' ') : '—'} />
          </dl>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Performance</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Perf label="Facturen" value={d.performance.aantal_facturen} />
            <Perf label="Betaald" value={d.performance.betaald} />
            <Perf label="Openstaand" value={d.performance.openstaand_aantal} />
            <Perf label="Verzekerd" value={d.performance.verzekerd_aantal} />
            <Perf label="Schades" value={d.performance.aantal_schades} />
            <Perf label="Goedgekeurd" value={d.performance.goedgekeurde_schades} />
            <Perf label="Schade-ratio" value={`${d.performance.schade_ratio_pct}%`} accent={d.performance.schade_ratio_pct > 25 ? 'text-mfb-orange' : 'text-slate-900'} />
            <Perf label="Dekkingsgraad" value={`${d.performance.dekkingsgraad_pct}%`} />
          </div>
        </div>
      </div>

      {/* Financiële kerncijfers — rustig & zakelijk (zoals Verrekeningen) */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-2">
        {[
          { l: 'Premie', v: fmt(f.premie), tone: 'border-mfb-blue/30 bg-mfb-blue/5' },
          { l: 'Servicefee', v: fmt(f.servicefee), tone: 'border-emerald-300 bg-emerald-50' },
          { l: 'Bijbetaling', v: fmt(f.bijbetaling), tone: 'border-mfb-orange/30 bg-mfb-orange/5' },
          { l: 'Losse verkoop', v: fmt(f.losse_verkoop || 0), tone: 'border-mfb-blue/30 bg-mfb-blue/5' },
          { l: 'Openstaand', v: fmt(f.openstaand), tone: 'border-amber-300 bg-amber-50' },
          { l: 'Verzekerd', v: fmt(f.verzekerd), tone: 'border-slate-200 bg-slate-50' },
        ].map((t) => (
          <div key={t.l} className={`rounded-2xl border p-5 ${t.tone}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{t.l}</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-slate-800">{t.v}</p>
          </div>
        ))}
      </div>
      {/* Onderbouwing: bijbetaling per actie (via afrekening) + losse verkoop (direct betaald) */}
      <div className="mb-6 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
        <span>
          <span className="font-medium text-slate-700">Bijbetaling</span> (via afrekening):{' '}
          {f.extra_opinies || 0} extra opinie(s) × {fmt(f.tarief_opinie || 0)} = {fmt(f.bijbetaling_opinies || 0)}
          <span className="text-slate-400"> · incasso wordt per stuk via de staffel afgerekend (zie Losse verkoop)</span>
        </span>
        <span>
          <span className="font-medium text-slate-700">Losse verkoop</span> (direct betaald):{' '}
          {f.losse_aantal || 0} bestelling(en)
          {f.losse_per_soort && Object.keys(f.losse_per_soort).length
            ? ' — ' + Object.entries(f.losse_per_soort).map(([k, v]: any) => `${k}: ${fmt(v)}`).join(' · ')
            : ''}
        </span>
      </div>

      {/* Verbruik */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {usageBar(f.opinies_gebruikt, f.inbegrepen_opinies, 'Opinies (kredietchecks)')}
        {usageBar(f.incassos_gebruikt, f.inbegrepen_incassos, "Incasso's / schades")}
      </div>

      {/* Debiteuren */}
      <Section title={`Debiteuren (${d.debtors.length})`} cols={['Bedrijf', 'KvK', 'Land', 'Grade', 'Max dekking', 'Status']}>
        {d.debtors.map((x: any) => (
          <tr key={x.id} className="hover:bg-slate-50">
            <td className="px-5 py-2 font-medium text-mfb-blue">{x.company_name}</td>
            <td className="px-5 py-2 text-slate-600">{x.company_number || '—'}</td>
            <td className="px-5 py-2 text-slate-600">{x.country_code}</td>
            <td className="px-5 py-2 text-slate-700">{x.grade ?? '—'}</td>
            <td className="px-5 py-2 text-slate-700">{x.dekking != null ? fmt(x.dekking) : '—'}</td>
            <td className="px-5 py-2 text-slate-600">{x.status}</td>
          </tr>
        ))}
        {d.debtors.length === 0 && <tr><td colSpan={6} className="px-5 py-4 text-slate-400">Geen debiteuren.</td></tr>}
      </Section>

      {/* Facturen */}
      <Section title={`Lopende facturen (${d.invoices.length})`} cols={['Factuur', 'Bedrag', 'Verzekerd', 'Vervaldatum', 'Status']}>
        {d.invoices.map((x: any) => (
          <tr key={x.id} className="hover:bg-slate-50">
            <td className="px-5 py-2 font-medium text-mfb-blue">{x.invoice_number}</td>
            <td className="px-5 py-2 text-slate-700">{fmt(x.amount_gross)}</td>
            <td className="px-5 py-2 text-slate-600">{fmt(x.coverage_amount)}</td>
            <td className="px-5 py-2 text-slate-600">{fmtDate(x.due_date)}</td>
            <td className="px-5 py-2 text-slate-600">{STATUS_LABELS[x.status] || x.status}</td>
          </tr>
        ))}
        {d.invoices.length === 0 && <tr><td colSpan={5} className="px-5 py-4 text-slate-400">Geen facturen.</td></tr>}
      </Section>

      {/* Kredietchecks */}
      <Section title={`Kredietchecks (${d.credit_checks.length})`} cols={['Grade', 'Verzekerbaar', 'Status', 'Datum']}>
        {d.credit_checks.map((x: any) => (
          <tr key={x.id} className="hover:bg-slate-50">
            <td className="px-5 py-2 font-medium text-mfb-blue">{x.rating ?? '—'}</td>
            <td className="px-5 py-2 text-slate-600">{x.is_insurable ? 'Ja' : 'Nee'}</td>
            <td className="px-5 py-2 text-slate-600">{x.status}</td>
            <td className="px-5 py-2 text-slate-600">{fmtDate(x.created_at)}</td>
          </tr>
        ))}
        {d.credit_checks.length === 0 && <tr><td colSpan={4} className="px-5 py-4 text-slate-400">Geen kredietchecks.</td></tr>}
      </Section>

      {/* Incasso's / schades */}
      <Section title={`Incasso's / schades (${d.claims.length})`} cols={['Dossier', 'Bedrag', 'Uitgekeerd', 'Status', 'Datum']}>
        {d.claims.map((x: any) => (
          <tr key={x.id} className="hover:bg-slate-50">
            <td className="px-5 py-2 font-medium text-mfb-blue">{x.claim_number}</td>
            <td className="px-5 py-2 text-slate-700">{fmt(x.claim_amount)}</td>
            <td className="px-5 py-2 text-slate-600">{x.approved_amount != null ? fmt(x.approved_amount) : '—'}</td>
            <td className="px-5 py-2 text-slate-600">{STATUS_LABELS[x.status] || x.status}</td>
            <td className="px-5 py-2 text-slate-600">{fmtDate(x.created_at)}</td>
          </tr>
        ))}
        {d.claims.length === 0 && <tr><td colSpan={5} className="px-5 py-4 text-slate-400">Geen incasso's / schades.</td></tr>}
      </Section>
    </Layout>
  );
};

const MEMBER_STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Actief lid', cls: 'bg-emerald-100 text-emerald-700' },
  suspended: { label: 'Geschorst', cls: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Geblokkeerd — SEPA vernieuwen', cls: 'bg-red-100 text-red-700' },
  inactive: { label: 'Wacht op goedkeuring', cls: 'bg-slate-100 text-slate-600' },
};

const MemberStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = MEMBER_STATUS[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-medium px-3 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
};

const MemberStatusActions: React.FC<{ memberId: string; token: string; status: string; rol: string; onChanged: () => void }> = ({ memberId, token, status, rol, onChanged }) => {
  const [busy, setBusy] = useState('');
  const notify = useToast();
  const isAdmin = rol === 'kva_admin';

  const doAction = async (actie: 'suspend' | 'block' | 'reactivate', bevestig?: string) => {
    if (bevestig && !window.confirm(bevestig)) return;
    setBusy(actie);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${memberId}/${actie}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Actie mislukt'); }
      const labels: Record<string, string> = { suspend: 'Het lid is geschorst.', block: 'Het lid is geblokkeerd.', reactivate: 'Het lid is weer geactiveerd.' };
      notify.success(labels[actie]);
      onChanged();
    } catch (e: any) { notify.error(e.message || 'Actie mislukt'); }
    finally { setBusy(''); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-1">Toegang</span>
      {status !== 'active' && (
        <button disabled={!!busy} onClick={() => doAction('reactivate')}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
          Heractiveren
        </button>
      )}
      {status !== 'suspended' && (
        <button disabled={!!busy} onClick={() => doAction('suspend')}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">
          Schorsen
        </button>
      )}
      {status !== 'blocked' && !isAdmin && (
        <button disabled={!!busy} onClick={() => doAction('block', 'Lid blokkeren? De SEPA-machtiging wordt ingetrokken; het lid moet die vernieuwen voordat verzekeren/incasso/checks weer werken.')}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
          Blokkeren
        </button>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-slate-900 font-medium text-right">{value}</dd>
  </div>
);

const Perf: React.FC<{ label: string; value: string | number; accent?: string }> = ({ label, value, accent }) => (
  <div>
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className={`text-xl font-bold ${accent || 'text-slate-900'}`}>{value}</p>
  </div>
);

const AfrekeningenSection: React.FC<{ memberId: string; token: string }> = ({ memberId, token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  const notify = useToast();

  const [verstuur, setVerstuur] = useState<{ soort: 'premie' | 'schade'; titel: string; bedrag: number } | null>(null);

  const load = React.useCallback(() => {
    fetch(`${API_BASE_URL}/api/admin/members/${memberId}/afrekeningen`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, token]);
  useEffect(() => { load(); }, [load]);

  const openVoorbeeld = async (soort: 'premie' | 'schade') => {
    setBusy(soort);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${memberId}/afrekening-preview/${soort}`, { headers });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setVerstuur({ soort, titel: d.titel, bedrag: d.bedrag });
    } catch { notify.error('Voorbeeld laden mislukt.'); } finally { setBusy(''); }
  };

  const verstuurActies = (v: { soort: 'premie' | 'schade'; titel: string; bedrag: number }): BeoordeelActie[] => [{
    key: 'versturen', label: 'Opstellen & versturen', kleur: v.soort === 'premie' ? 'green' : 'amber',
    template_key: 'afrekening_beschikbaar', bijlage: `${v.titel} (PDF)`,
    context: (_r, _t) => ({ titel: v.titel, bedrag: fmt(v.bedrag) }),
  }];

  const voerVerstuurUit = async (soort: string, onderwerp: string, bericht: string) => {
    const r = await fetch(`${API_BASE_URL}/api/admin/members/${memberId}/afrekeningen/${soort}`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); notify.error(e.detail || 'Afrekening opstellen mislukt.'); return null; }
    notify.success('Afrekening opgesteld en naar het ledenportaal gepusht (met bericht).');
    load();
    return r.json();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-lg font-semibold text-mfb-blue">Afrekeningen</h2>
        <div className="flex gap-2">
          <button disabled={busy === 'premie'} onClick={() => openVoorbeeld('premie')}
            className="px-4 py-2 bg-mfb-blue hover:bg-mfb-blue-dark text-white rounded-lg text-sm font-semibold disabled:opacity-50">
            Premieafrekening opstellen…
          </button>
          <button disabled={busy === 'schade'} onClick={() => openVoorbeeld('schade')}
            className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg text-sm font-semibold disabled:opacity-50">
            Schade-afrekening opstellen…
          </button>
        </div>
      </div>

      {verstuur && (
        <BeoordelingModal
          titel={`${verstuur.titel} — voorbeeld & versturen`}
          context={`Bedrag ${fmt(verstuur.bedrag)} · gaat naar het ledenportaal + e-mail`}
          acties={verstuurActies(verstuur)} token={token}
          uitvoeren={(_a, _r, _t, ond, ber) => voerVerstuurUit(verstuur.soort, ond, ber)}
          onClose={() => setVerstuur(null)} onDone={() => setVerstuur(null)}
        />
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">Nog geen afrekeningen verstuurd. Een afrekening verschijnt direct in het ledenportaal en als bericht.</p>
      ) : (
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead><tr>{['Soort', 'Titel', 'Periode', 'Bedrag', 'Verstuurd'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${a.soort === 'premie' ? 'bg-mfb-blue/10 text-mfb-blue' : 'bg-mfb-orange/10 text-mfb-orange'}`}>{a.soort}</span></td>
                <td className="px-3 py-2 text-slate-700">{a.titel}</td>
                <td className="px-3 py-2 text-slate-600">{a.periode}</td>
                <td className="px-3 py-2 text-slate-700 tabular-nums">{fmt(a.bedrag)}</td>
                <td className="px-3 py-2 text-slate-500">{a.verstuurd_op}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminMemberDetailPage;
